import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { faFileCheck, faFilePen } from '@fortawesome/pro-light-svg-icons';
import {
  UseOfForceAssignmentOptionModel,
  UseOfForceAuthorizationRequestModel,
  UseOfForceAuthorizationRequestStatus,
  UseOfForceIncidentModel,
  UseOfForceIncidentWarning,
  UseOfForceInterventionType,
} from '../../../../api-service/clients';
import { AreaAside, AreaContainer, AreaContent, FloatingActionButton, FloatingActionButtonBar } from '../../../../components/area-layout';
import { CardComponent } from '../../../../components/card-component';
import { ColorPalette } from '../../../../components/config';
import { WindowService } from '../../../../components/window-service/window-service';
import { useBroadcastContext, useMetaTitle } from '../../../../hooks';
import { useIsAllowed } from '../../../../authentication/use-is-allowed';
import { ResourceOperations, ResourceTypes } from '../../../../authentication/resources';
import { formatDate, formatDateTime } from '../../../../utilities';
import { useServiceProviderCitizenContext } from '../../citizen-context';
import { ServiceProviderRoute, ServiceProviderUrlBuilder } from '../utils/service-provider-url-builder';
import {
  createUseOfForceAuthorizationRequest,
  createUseOfForceIncident,
  getUseOfForceAssignmentOptions,
  getUseOfForceAuthorizationRequests,
  getUseOfForceIncidents,
  registerUseOfForceAuthorizationRequestDecision,
  registerUseOfForceAuthorizationRequestMunicipalitySending,
  updateUseOfForceIncidentDraft,
} from './use-of-force-api';
import { UseOfForceRadarCard } from '../ai-demo/use-of-force-radar-card';
import { UseOfForceMunicipalitySendingDialog } from '../../../service-provider-use-of-force/use-of-force-municipality-sending-dialog';
import {
  UseOfForceAuthorizationDecisionDialog,
  UseOfForceAuthorizationDecisionSubject,
} from '../../authority/use-of-force-assessment/use-of-force-authorization-decision-dialog';
import { decisionMakerFor, maxPeriodMonths } from './use-of-force-authorization-rules';
import { UseOfForceAuthorizationsCard } from './use-of-force-authorizations-card';
import { UseOfForceHygieneRestraintsCard } from './use-of-force-hygiene-restraints-card';
import { ServiceProviderOmnibarAction, useOmnibarAction } from '../hooks/use-omnibar-action';
import { authorizationRequestStatusLabels, authorizationTypeShortLabels, interventionTypeShortLabels, statusLabels } from './use-of-force-labels';

export function UseOfForceTabContent() {
  const { citizenId, fullName, currentServiceProvider } = useServiceProviderCitizenContext();
  const serviceProviderUnitId = currentServiceProvider?.id;
  const canRegister = useIsAllowed([{ resourceType: ResourceTypes.UseOfForceIncident, resourceOperation: ResourceOperations.Write }]);
  const { subscribe } = useBroadcastContext('UseOfForceUpdate');
  const [incidents, setIncidents] = useState<UseOfForceIncidentModel[]>([]);
  const [reloadCount, setReloadCount] = useState(0);
  const [assignmentOptions, setAssignmentOptions] = useState<UseOfForceAssignmentOptionModel[]>();
  const [isCreating, setIsCreating] = useState(false);
  const [authorizationRequests, setAuthorizationRequests] = useState<UseOfForceAuthorizationRequestModel[]>([]);
  const [canRequestAuthorization, setCanRequestAuthorization] = useState(false);
  const [municipalitySendingRequest, setMunicipalitySendingRequest] = useState<UseOfForceAuthorizationRequestModel>();
  const [decidingRequest, setDecidingRequest] = useState<UseOfForceAuthorizationRequestModel>();
  const prefilledStartedAt = useRef<Date | string>();

  useMetaTitle(`Magtanvendelse - ${fullName}`);

  useEffect(() => {
    subscribe(() => setReloadCount(count => count + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!serviceProviderUnitId) return undefined;
    let isCurrent = true;
    getUseOfForceIncidents(citizenId, serviceProviderUnitId).then(result => isCurrent && setIncidents(result));
    async function loadAuthorizationRequests(unitId: string) {
      const result = await getUseOfForceAuthorizationRequests(citizenId, unitId);
      if (!isCurrent) return;
      setCanRequestAuthorization(result.canAdminister);
      setAuthorizationRequests(result.requests);
    }
    loadAuthorizationRequests(serviceProviderUnitId);
    return () => {
      isCurrent = false;
    };
  }, [citizenId, serviceProviderUnitId, reloadCount]);

  useOmnibarAction(ServiceProviderOmnibarAction.RegisterUseOfForce, () => handleRegister(serviceProviderUnitId as string), !!serviceProviderUnitId && !!canRegister);
  useOmnibarAction(
    ServiceProviderOmnibarAction.RequestUseOfForceAuthorization,
    () => requestAuthorization(serviceProviderUnitId as string),
    !!serviceProviderUnitId && canRequestAuthorization
  );

  if (!serviceProviderUnitId) return null;

  function openIncident(unitId: string, incidentId: string) {
    WindowService.open(ServiceProviderUrlBuilder.build(ServiceProviderRoute.USE_OF_FORCE_INCIDENT, { citizenId, spuId: unitId, incidentId }));
  }

  function create(unitId: string, assignmentId: string | undefined) {
    setAssignmentOptions(undefined);
    setIsCreating(true);
    createUseOfForceIncident(citizenId, unitId, assignmentId)
      .then(incident => prefillStartedAt(unitId, incident))
      .then(incident => {
        setIncidents(current => [incident, ...current]);
        openIncident(unitId, incident.id);
      })
      .finally(() => setIsCreating(false));
  }

  function openAuthorizationRequest(unitId: string, authorizationRequestId: string) {
    WindowService.open(ServiceProviderUrlBuilder.build(ServiceProviderRoute.USE_OF_FORCE_AUTHORIZATION_REQUEST, { citizenId, spuId: unitId, authorizationRequestId }));
  }

  async function requestAuthorization(unitId: string) {
    setIsCreating(true);
    try {
      const request = await createUseOfForceAuthorizationRequest(citizenId, unitId);
      setAuthorizationRequests(current => [request, ...current]);
      openAuthorizationRequest(unitId, request.id);
    } finally {
      setIsCreating(false);
    }
  }

  // The radar only suggests when it happened; the account of the episode stays the employee's own.
  async function prefillStartedAt(unitId: string, incident: UseOfForceIncidentModel) {
    const interventionStartedAt = prefilledStartedAt.current;
    prefilledStartedAt.current = undefined;
    if (!interventionStartedAt) return incident;
    return updateUseOfForceIncidentDraft(citizenId, unitId, incident.id, {
      ...incident.draft,
      interventionStartedAt,
    });
  }

  function handleRegister(unitId: string, interventionStartedAt?: Date | string) {
    prefilledStartedAt.current = interventionStartedAt;
    setIsCreating(true);
    getUseOfForceAssignmentOptions(citizenId, unitId)
      .then(options => {
        if (options.length > 1) {
          setIsCreating(false);
          setAssignmentOptions(options);
        } else {
          create(unitId, options[0]?.assignmentId);
        }
      })
      .catch(() => setIsCreating(false));
  }

  return (
    <AreaContainer>
      <AreaContent>
        <UseOfForceRadarCard
          citizenId={citizenId}
          serviceProviderUnitId={serviceProviderUnitId}
          canRegister={canRegister}
          onRegister={interventionStartedAt => handleRegister(serviceProviderUnitId, interventionStartedAt)}
          onOpenIncident={incidentId => openIncident(serviceProviderUnitId, incidentId)}
        />
        <CardComponent title="Registrerede magtanvendelser" contentBackgroundColor={ColorPalette.WHITE}>
          {incidents.length === 0 ? (
            <Typography variant="body2">Der er ingen registrerede magtanvendelser for borgeren.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Indgreb påbegyndt</TableCell>
                  <TableCell>Indgreb</TableCell>
                  <TableCell>Skema</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Frist</TableCell>
                  <TableCell>Registreret af</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidents.map(incident => (
                  <TableRow
                    key={incident.id}
                    hover
                    onClick={() => openIncident(serviceProviderUnitId, incident.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{incident.draft.interventionStartedAt ? formatDateTime(incident.draft.interventionStartedAt) : '-'}</TableCell>
                    <TableCell>{incident.draft.interventionType ? interventionTypeShortLabels[incident.draft.interventionType] : '-'}</TableCell>
                    <TableCell>Skema 1</TableCell>
                    <TableCell>{statusLabels[incident.status]}</TableCell>
                    <TableCell sx={incident.warnings.includes(UseOfForceIncidentWarning.RegistrationDeadlineExceeded) ? { color: 'error.main' } : undefined}>
                      {formatDate(registrationDeadline(incident.draft.interventionStartedAt), undefined, '-')}
                    </TableCell>
                    <TableCell>{incident.createdByName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardComponent>
        {canRequestAuthorization && (
          <CardComponent title="Anmodninger om forhåndsgodkendelse" contentBackgroundColor={ColorPalette.WHITE}>
            {authorizationRequests.length === 0 ? (
              <Typography variant="body2">Der er ingen anmodninger om forhåndsgodkendelse for borgeren.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Indgreb</TableCell>
                    <TableCell>Forventet periode</TableCell>
                    <TableCell>Skema</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sendt</TableCell>
                    <TableCell>Handlekommune</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {authorizationRequests.map(request => (
                    <TableRow key={request.id} hover onClick={() => openAuthorizationRequest(serviceProviderUnitId, request.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell>{request.draft.authorizationType ? authorizationTypeShortLabels[request.draft.authorizationType] : '-'}</TableCell>
                      <TableCell>
                        {request.draft.periodFrom && request.draft.periodTo ? `${formatDate(request.draft.periodFrom)} - ${formatDate(request.draft.periodTo)}` : '-'}
                      </TableCell>
                      <TableCell>Skema 3</TableCell>
                      <TableCell onClick={event => event.stopPropagation()}>
                        {authorizationRequestStatusLabels[request.status]}
                        {awaitsDecisionFromAnotherKommune(request) && (
                          <Typography variant="caption" display="block">
                            <Button size="small" onClick={() => setDecidingRequest(request)}>
                              Registrér afgørelse
                            </Button>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{request.sentAt ? formatDateTime(request.sentAt) : '-'}</TableCell>
                      <TableCell onClick={event => event.stopPropagation()}>
                        {request.sentAt ? (request.responsibleMunicipalityName ?? '-') : '-'}
                        {request.sentAt && (
                          <Typography variant="caption" display="block">
                            {awaitsSendingToExternalMunicipality(request) ? (
                              <Button size="small" onClick={() => setMunicipalitySendingRequest(request)}>
                                Registrér afsendelse
                              </Button>
                            ) : (
                              formatDate(request.sentToResponsibleMunicipalityAt, 'dd-MM-yyyy HH:mm', '-')
                            )}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardComponent>
        )}
        <UseOfForceHygieneRestraintsCard
          citizenId={citizenId}
          serviceProviderUnitId={serviceProviderUnitId}
          reloadCount={reloadCount}
          canRegister={canRegister}
          hasSection136cIncident={incidents.some(incident => incident.draft.interventionType === UseOfForceInterventionType.Section136c)}
        />
        <UseOfForceAuthorizationsCard citizenId={citizenId} serviceProviderUnitId={serviceProviderUnitId} reloadCount={reloadCount} />
      </AreaContent>
      {(canRegister || canRequestAuthorization) && (
        <AreaAside>
          <FloatingActionButtonBar>
            {canRegister && (
              <FloatingActionButton
                buttonText="Registrér magtanvendelse"
                icon={faFilePen}
                disabled={isCreating}
                onClick={() => handleRegister(serviceProviderUnitId)}
              />
            )}
            {canRequestAuthorization && (
              <FloatingActionButton
                buttonText="Anmod om forhåndsgodkendelse"
                icon={faFileCheck}
                disabled={isCreating}
                onClick={() => requestAuthorization(serviceProviderUnitId)}
              />
            )}
          </FloatingActionButtonBar>
        </AreaAside>
      )}
      {assignmentOptions && (
        <ChooseAssignmentDialog
          options={assignmentOptions}
          onChoose={assignmentId => create(serviceProviderUnitId, assignmentId)}
          onClose={() => setAssignmentOptions(undefined)}
        />
      )}
      {municipalitySendingRequest && (
        <UseOfForceMunicipalitySendingDialog
          municipalityName={municipalitySendingRequest.responsibleMunicipalityName}
          onSave={async sentAt => {
            await registerUseOfForceAuthorizationRequestMunicipalitySending(
              { citizenId, serviceProviderUnitId, requestId: municipalitySendingRequest.id },
              sentAt
            );
            setMunicipalitySendingRequest(undefined);
            setReloadCount(count => count + 1);
          }}
          onClose={() => setMunicipalitySendingRequest(undefined)}
        />
      )}
      {decidingRequest && (
        <UseOfForceAuthorizationDecisionDialog
          request={decisionSubject(decidingRequest)}
          isFromAnotherKommune
          onSave={async decision => {
            await registerUseOfForceAuthorizationRequestDecision({ citizenId, serviceProviderUnitId, requestId: decidingRequest.id }, decision);
            setDecidingRequest(undefined);
            setReloadCount(count => count + 1);
          }}
          onClose={() => setDecidingRequest(undefined)}
        />
      )}
    </AreaContainer>
  );
}

function awaitsSendingToExternalMunicipality(request: UseOfForceAuthorizationRequestModel): boolean {
  return request.responsibleMunicipalityIsOwnMunicipality === false && !request.sentToResponsibleMunicipalityAt;
}

// Another kommune decides outside Fasit, so the leader registers its afgørelse from the letter (decision 63).
function awaitsDecisionFromAnotherKommune(request: UseOfForceAuthorizationRequestModel): boolean {
  return request.status === UseOfForceAuthorizationRequestStatus.Sent && request.responsibleMunicipalityIsOwnMunicipality === false;
}

function decisionSubject(request: UseOfForceAuthorizationRequestModel): UseOfForceAuthorizationDecisionSubject {
  const authorizationType = request.draft.authorizationType;
  return {
    authorizationType,
    requestedPeriodFrom: request.draft.periodFrom,
    requestedPeriodTo: request.draft.periodTo,
    maxPeriodMonths: authorizationType ? maxPeriodMonths(authorizationType) : undefined,
    decisionMaker: authorizationType ? decisionMakerFor(authorizationType) : undefined,
  };
}

// BEK 1411 § 21: registration is due no later than three days after the intervention.
export function registrationDeadline(interventionStartedAt: Date | string | undefined): Date | undefined {
  if (!interventionStartedAt) return undefined;
  const deadline = new Date(interventionStartedAt);
  deadline.setDate(deadline.getDate() + 3);
  return deadline;
}

interface ChooseAssignmentDialogProps {
  options: UseOfForceAssignmentOptionModel[];
  onChoose: (assignmentId: string) => void;
  onClose: () => void;
}

function ChooseAssignmentDialog(props: ChooseAssignmentDialogProps) {
  const { options, onChoose, onClose } = props;
  const [chosen, setChosen] = useState(options[0].assignmentId);

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Vælg bestilling</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Borgeren har flere aktuelle bestillinger hos enheden. Skemaet forudfyldes med oplysninger fra den valgte.
        </Typography>
        <RadioGroup value={chosen} onChange={event => setChosen(event.target.value)}>
          {options.map(option => (
            <FormControlLabel
              key={option.assignmentId}
              value={option.assignmentId}
              control={<Radio />}
              label={[option.name, option.startDate ? `fra ${formatDate(option.startDate)}` : undefined].filter(Boolean).join(' ')}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annullér</Button>
        <Button variant="contained" onClick={() => onChoose(chosen)}>
          Opret skema
        </Button>
      </DialogActions>
    </Dialog>
  );
}
